import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Alert, Empty, ErrorState, Skeleton } from '@/components/States';
import { dateTime } from '@/lib/format';
import { LegacyLink, ReadonlyNote } from './kit/Kit';
import { useBack } from './kit/hooks';
import { useAccountsAndRoles } from './usersData';
import { RoleEditor } from './RoleEditor';
import { RoleDeleteFlow } from './RoleDeleteFlow';
import {
  ALL_PERMS, GENERIC_LABEL, GROUPS, PERM_LABEL, RESTRICTION_DENIES, HIDE_SALES_USER,
  dataScopes, effectivePerms, groupCodes, overriddenPerms, sensitiveFields, summarizeGroup, summarizeRole,
  type GenericAction,
} from './permissions';
import { baseRoleLabel, roleKind } from './status';
import type { Role } from './types';
import s from './RoleDetail.module.css';

const fieldMaskRestrictions = new Set([
  '不能查看产品单价和明细金额', '不能查看客户联系方式',
  '不能查看产品成本与入库均价', '不能查看供应商报价',
]);

/** 人话摘要：「能做的」绿底、「不能做的」红底（文档 20 §4 ④）。给业务负责人审核用 */
export function SummaryBlocks({ role, title }: { role: Role; title?: string }) {
  const sm = summarizeRole(role);
  return (
    <section className={s.summary} aria-label={title || '能干什么'}>
      {title && <h3 className={s.sumTitle}>{title}<small>由当前权限自动生成，不是手写的</small></h3>}
      <div className={s.sumGrid}>
        <div className={s.can}>
          <b><Icon name="ok" size={14} />能做的</b>
          {sm.can.length ? <ul>{sm.can.map((x) => <li key={x}>{x}</li>)}</ul> : <p>没有可用的功能。</p>}
        </div>
        <div className={sm.cannot.length ? s.cannot : s.cannotNone}>
          <b><Icon name="x" size={14} />不能做的</b>
          {sm.cannot.length ? <ul>{sm.cannot.map((x) => <li key={x}>{x}</li>)}</ul> : <p>没有被收回的功能，也没有隐藏的字段。</p>}
        </div>
      </div>
    </section>
  );
}

/** 摘要一句话（卡片、表格里用）：红字是「看不到…」 */
export function Headline({ role, clamp }: { role: Role; clamp?: boolean }) {
  const sm = summarizeRole(role);
  return (
    <span className={clamp ? s.clamp : undefined}>
      {sm.headline}
      {sm.hidden.length > 0 && <em className={s.red}> 看不到{sm.hidden.join('和')}。</em>}
    </span>
  );
}

function Mark({ on }: { on: boolean }) {
  return on ? <span className={s.on} aria-label="有"><Icon name="check" size={14} /></span> : <span className={s.offMark} aria-label="没有">—</span>;
}

/** ① 功能权限：11 个资源分组，每组一行「资源名 + 已开数 + 一句人话」，展开看通用动作和专项操作 */
function Groups({ perms, overridden }: { perms: Set<string>; overridden: Set<string> }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const allOpen = open.size === GROUPS.length;
  return (
    <section className={s.sec}>
      <header className={s.sh}>
        <h3>① 功能权限<small>每组一句人话摘要</small></h3>
        <button className={s.link} onClick={() => setOpen(allOpen ? new Set() : new Set(GROUPS.map((g) => g.key)))}>{allOpen ? '全部收起' : '全部展开'}</button>
      </header>
      <div className={s.glist}>
        {GROUPS.map((g) => {
          const sm = summarizeGroup(g, perms);
          const isOpen = open.has(g.key);
          const genericCodes = new Set(Object.values(g.generic || {}).flat());
          const special = g.special.filter((c) => !genericCodes.has(c));
          return (
            <div key={g.key} className={`${s.grow} ${isOpen ? s.gopen : ''}`}>
              <button className={s.ghead} aria-expanded={isOpen} onClick={() => { const n = new Set(open); if (isOpen) n.delete(g.key); else n.add(g.key); setOpen(n); }}>
                <span className={s.gname}><b>{g.name}</b><small>{g.sub}</small></span>
                <span className={`tnum ${s.gcount} ${sm.none ? s.zero : ''}`}>{sm.on} / {sm.total}</span>
                <span className={s.gsum}>
                  {sm.none ? <em className={s.red}>{g.key === 'system' ? '不能管账号、角色和设置' : `看不到${g.name}`}</em>
                    : sm.readOnly ? <>只能看{sm.cannot.length > 0 && <em className={s.red}>，不能{sm.cannot.slice(0, 3).join('、')}</em>}</>
                    : <>能{sm.can.slice(0, 4).join('、')}{sm.can.length > 4 ? ` 等 ${sm.can.length} 项` : ''}{sm.cannot.length > 0 && <em className={s.red}>；不能{sm.cannot.slice(0, 3).join('、')}</em>}</>}
                </span>
                <Icon name={isOpen ? 'chevu' : 'chevd'} className={s.gchev} />
              </button>
              {isOpen && (
                <div className={s.gbody}>
                  {g.generic ? (
                    <div className={s.generic}>
                      <span className={s.glabel}>通用动作</span>
                      <div className={s.blocks}>
                        {(Object.keys(GENERIC_LABEL) as GenericAction[]).map((a) => {
                          const codes = g.generic?.[a];
                          if (!codes) return <span key={a} className={s.na} title="这个资源没有这个动作">{GENERIC_LABEL[a]}<i>—</i></span>;
                          const on = codes.some((c) => perms.has(c));
                          return <span key={a} className={on ? s.bon : s.boff} title={codes.join(' / ')}>{GENERIC_LABEL[a]}<Mark on={on} /></span>;
                        })}
                      </div>
                    </div>
                  ) : null}
                  {(special.length > 0 || !g.generic) && (
                    <ul className={s.items}>
                      {(g.generic ? special : groupCodes(g)).map((c) => (
                        <li key={c} className={perms.has(c) ? '' : s.itemOff}>
                          <Mark on={perms.has(c)} />
                          <span>{PERM_LABEL[c]}<code>{c}</code></span>
                          {overridden.has(c) && <em className={s.red}>已授予，被限制项压掉</em>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {g.key === 'orders' && <p className={s.gnote}>看哪些订单见「② 数据范围」，订单金额见「③ 敏感字段」。</p>}
                  {g.key === 'inventory' && <p className={s.gnote}>库存没有「新建 / 编辑 / 删除」，它的写操作都在专项里（入库 / 出库 / 调整 / 期初导入）。</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function RoleDetail({ id, embedded, onDeleted }: { id: string; embedded?: boolean; onDeleted?: () => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(() => !embedded && searchParams.get('edit') === '1');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const back = useBack('/users');
  const data = useAccountsAndRoles();
  const role = data.roles.find((r) => r.id === id || r.code === id);

  if (data.rolesState.error) return <ErrorState title="角色加载失败" message={data.rolesState.error.message} detail="GET /api/roles" onRetry={data.rolesState.reload} />;
  if (!role) {
    if (data.rolesState.loading || data.usersState.loading) return <div className={s.loading}><Skeleton /><Skeleton lines={6} /></div>;
    return <Empty icon="info" title="找不到这个角色">可能已被删除，或你没有「查看角色列表」权限。</Empty>;
  }

  const perms = effectivePerms(role);
  const overridden = new Set(overriddenPerms(role));
  const kind = roleKind(role.isPreset);
  const members = data.canUsers ? data.membersOf(role) : [];
  const granted = ALL_PERMS.filter((p) => perms.has(p)).length;
  const scopes = dataScopes(role, perms);
  const sens = sensitiveFields(role, perms);
  const phone = ff === 'phone';
  const canEdit = role.isPreset ? can('roles.preset.manage') : can('roles.edit');
  const leaveEditor = () => {
    setEditing(false);
    if (searchParams.has('edit')) {
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${phone ? s.phone : ''}`}>
      {!editing && <header className={s.head}>
        {!embedded && <button className={s.back} onClick={back} aria-label="返回"><Icon name="back" size={20} /></button>}
        <h1>{role.name}</h1>
        <Tag tone={kind.tone} sm>{kind.label}</Tag>
        {!role.isActive && <Dot tone="grey">已停用</Dot>}
        <span className={s.sp} />
        {embedded && <Link className={s.open} to={`/users/roles/${role.id}`}>全屏查看</Link>}
        {can('audit.read') && can('roles.read') && <Link className={s.open} to="/users/history">变更记录</Link>}
        {canEdit && <button type="button" className={s.editButton} onClick={() => { if (editing) leaveEditor(); else if (embedded && ff === 'tablet') navigate(`/users/roles/${role.id}?edit=1`); else setEditing(true); }}>{editing ? '查看权限' : '编辑权限'}</button>}
        {can('roles.delete') && !role.isPreset && <button type="button" className={s.deleteButton} disabled={role.userCount > 0 && data.usersState.loading} onClick={() => setDeleteOpen(true)}>删除角色</button>}
        {can('roles.edit', 'roles.create', 'roles.delete') && <LegacyLink path="/users">{phone ? '老版' : '打开老版'}</LegacyLink>}
      </header>}
      {editing ? <RoleEditor key={`${role.id}:${role.updatedAt}`} role={role} members={members.map((item) => item.name)} onCancel={leaveEditor} onSaved={() => { leaveEditor(); data.rolesState.reload(); }} /> : <>
      <div className={s.scroll}>
        <p className={s.sub}>
          <span className="tnum">{role.code}</span> · 模板 {baseRoleLabel[role.baseRole] || role.baseRole} · 生效 <b className="tnum">{granted}</b> / {ALL_PERMS.length} 项
          {' · '}{role.userCount} 个账号在用{role.updatedAt && <> · 更新于 {dateTime(role.updatedAt)}</>}
        </p>
        {role.description && <p className={s.desc}>{role.description}</p>}
        {!role.isActive && <Alert tone="danger" icon="lock" title="角色已停用">使用这个角色的账号没有任何权限（老系统按角色停用直接清空权限）。</Alert>}

        <SummaryBlocks role={role} title="能干什么" />

        <Groups perms={perms} overridden={overridden} />

        <section className={s.sec}>
          <header className={s.sh}><h3>② 数据范围<small>能看到哪些单据</small></h3></header>
          <div className={s.scopes}>
            {scopes.map((sc) => (
              <div key={sc.key} className={s.scope}>
                <span className={s.slabel}>{sc.label}</span>
                <div className={s.opts}>
                  {sc.options.map((o) => <span key={o.label} className={o.on ? s.optOn : s.opt}>{o.on && <Icon name="check" size={14} />}{o.label}</span>)}
                </div>
                {sc.note && <p className={s.snote}>{sc.note}</p>}
              </div>
            ))}
          </div>
        </section>

        <section className={s.sec}>
          <header className={s.sh}><h3>③ 敏感字段<small>关掉 = 列表和详情里整块不出现，不是打码</small></h3></header>
          <ul className={s.sens}>
            {sens.map((f) => (
              <li key={f.label}>
                <span><b>{f.label}</b><small>{f.desc}</small></span>
                <span className={s.sstate}>{f.visible ? <Dot tone="green">可见</Dot> : <Dot tone="red">隐藏</Dot>}<code>{f.source}</code></span>
              </li>
            ))}
          </ul>
        </section>

        {role.restrictions.length > 0 && (
          <section className={s.sec}>
            <header className={s.sh}><h3>限制项 {role.restrictions.length} 条<small>老系统的显式拒绝，永远压倒授权</small></h3></header>
            <ul className={s.restr}>
              {role.restrictions.map((r) => {
                const denied = RESTRICTION_DENIES[r];
                return (
                  <li key={r}>
                    <b>{r}</b>
                    <small>{r === HIDE_SALES_USER ? '字段脱敏：订单里不出现业务员（不走权限码）' : fieldMaskRestrictions.has(r) ? `字段脱敏：共享服务端隐藏对应字段${denied?.length ? `，并收回 ${denied.join('、')}` : ''}` : denied ? `收回 ${denied.length} 个权限码${denied.length <= 4 ? `：${denied.join('、')}` : ''}` : '不在映射表里，老系统不生效'}</small>
                  </li>
                );
              })}
            </ul>
            {overridden.size > 0 && <p className={s.gnote}>有 {overridden.size} 项「已授予」被限制项压掉，上面的功能权限按压掉后的结果显示。</p>}
          </section>
        )}

        {data.canUsers && (
          <section className={s.sec}>
            <header className={s.sh}><h3>使用这个角色的账号<small className="tnum">{members.length} 个</small></h3></header>
            {members.length ? (
              <div className={s.members}>
                {members.map((u) => (
                  <Link key={u.id} to={`/users/accounts/${u.id}`} className={u.isActive ? s.member : `${s.member} ${s.memberOff}`}>
                    <i>{u.name.slice(0, 1)}</i>{u.name}{!u.isActive && <small>已停用</small>}
                  </Link>
                ))}
              </div>
            ) : <p className={s.none}>没有账号在用这个角色。</p>}
          </section>
        )}
        <ReadonlyNote>生产默认只读；本地可核对权限草稿、角色删除和账号迁移流程。预置角色不可删除。</ReadonlyNote>
      </div>
      </>}
      {deleteOpen && <RoleDeleteFlow role={role} roles={data.roles} members={members} canMigrate={can('users.edit')} onClose={() => setDeleteOpen(false)} onAccountsChanged={() => { data.usersState.reload(); data.rolesState.reload(); }} onDeleted={() => { setDeleteOpen(false); onDeleted?.(); navigate('/users'); }} />}
    </div>
  );
}

export function RoleDetailPage() {
  const { id } = useParams();
  return <RoleDetail id={id!} />;
}
